import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, RefreshCw, Calendar, Check, Link2, Eye, Banknote } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";
import { todayCR } from "../utils/dateHelpers";

const fmtCRC = (n) => `₡${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BankTransfersTab({ selectedBox, configured }) {
  const [transfers, setTransfers] = useState([]);
  const [verifiedIds, setVerifiedIds] = useState(new Set());
  const [fetching, setFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [filterMode, setFilterMode] = useState("today");
  const [days, setDays] = useState("7");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [debugMode, setDebugMode] = useState(false);

  // Match modal
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchTransfer, setMatchTransfer] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Raw email body modal
  const [bodyModalOpen, setBodyModalOpen] = useState(false);
  const [bodyContent, setBodyContent] = useState(null);

  // IBAN config (per box)
  const [ibanModalOpen, setIbanModalOpen] = useState(false);
  const [ibanInput, setIbanInput] = useState("");

  useEffect(() => {
    if (selectedBox) setIbanInput(selectedBox.bank_account_iban || "");
  }, [selectedBox]);

  const handleSaveIban = async () => {
    try {
      const clean = (ibanInput || "").trim().toUpperCase().replace(/\s/g, "");
      await axiosInstance.patch(`/boxes/${selectedBox.id}/bank-iban?iban=${encodeURIComponent(clean)}`);
      toast.success("IBAN guardado. Vuelva a consultar para refrescar la dirección.");
      if (selectedBox) selectedBox.bank_account_iban = clean;
      setIbanModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al guardar IBAN"));
    }
  };

  useEffect(() => {
    const today = todayCR();
    setDateFrom(today);
    setDateTo(today);
  }, []);

  useEffect(() => {
    if (selectedBox) setTransfers([]);
  }, [selectedBox]);

  const handleFetch = async () => {
    if (!selectedBox) {
      toast.error("Seleccione una caja");
      return;
    }
    setFetching(true);
    try {
      let url = `/transfers/fetch?box_id=${selectedBox.id}`;
      if (debugMode) url += "&debug=true";
      if (filterMode === "today") {
        url += "&days=0";
      } else if (filterMode === "days") {
        url += `&days=${days}`;
      } else if (filterMode === "range") {
        if (!dateFrom) {
          toast.error("Seleccione fecha inicio");
          setFetching(false);
          return;
        }
        url += `&date_from=${dateFrom}`;
        if (dateTo) url += `&date_to=${dateTo}`;
      }
      const resp = await axiosInstance.get(url, { timeout: 60000 });
      setTransfers(resp.data.transfers || []);
      setVerifiedIds(new Set(resp.data.verified_email_ids || []));
      toast.success(`${resp.data.total} transferencias encontradas`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al consultar transferencias"));
    } finally {
      setFetching(false);
    }
  };

  const openMatchModal = async (transfer) => {
    setMatchTransfer(transfer);
    setMatchModalOpen(true);
    setLoadingCandidates(true);
    try {
      // Pull candidate transactions in a +/- 1 day window of the transfer date
      let from = "";
      let to = "";
      if (transfer.fecha) {
        // fecha viene como DD/MM/YYYY
        const [d, m, y] = transfer.fecha.split("/");
        const iso = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
        from = iso;
        to = iso;
      }
      const url = `/transfers/candidate-transactions?box_id=${selectedBox.id}` +
        (from ? `&date_from=${from}` : "") +
        (to ? `&date_to=${to}` : "");
      const resp = await axiosInstance.get(url);
      setCandidates(resp.data.transactions || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error cargando transacciones"));
      setCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleVerify = async (transactionId) => {
    if (!matchTransfer) return;
    try {
      await axiosInstance.post("/transfers/match", {
        email_id: matchTransfer.email_id,
        transaction_id: transactionId,
        box_id: selectedBox.id,
        transfer_data: matchTransfer,
      });
      toast.success("Transferencia verificada");
      setVerifiedIds((prev) => new Set([...prev, matchTransfer.email_id]));
      setMatchModalOpen(false);
      setMatchTransfer(null);
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al verificar"));
    }
  };

  const handleUnverify = async (transfer) => {
    if (!window.confirm("¿Quitar la verificación de esta transferencia?")) return;
    try {
      await axiosInstance.delete(`/transfers/match/${encodeURIComponent(transfer.email_id)}?box_id=${selectedBox.id}`);
      toast.success("Verificación removida");
      setVerifiedIds((prev) => {
        const next = new Set(prev);
        next.delete(transfer.email_id);
        return next;
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Error al remover verificación"));
    }
  };

  const openBody = (transfer) => {
    setBodyContent({
      from: transfer.from_email,
      subject: transfer.raw_subject,
      body: transfer.raw_body || "(sin cuerpo)",
    });
    setBodyModalOpen(true);
  };

  const filtered = transfers.filter((t) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (t.beneficiario || "").toLowerCase().includes(s) ||
      (t.documento || "").includes(searchTerm) ||
      (t.cuenta_origen || "").toLowerCase().includes(s) ||
      (t.cuenta_destino || "").toLowerCase().includes(s) ||
      (t.detalle || "").toLowerCase().includes(s)
    );
  });

  const totalAmount = transfers.reduce((s, t) => s + (t.monto || 0), 0);
  const verifiedCount = transfers.filter((t) => verifiedIds.has(t.email_id)).length;

  if (!configured) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-6 text-center">
          <p className="text-amber-700 font-medium">Configure el correo en la pestaña de SINPE primero.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="transfers-tab">
      {/* Fetch Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex gap-1">
              <Button size="sm" variant={filterMode === "today" ? "default" : "outline"} onClick={() => setFilterMode("today")} data-testid="transfers-filter-today">Hoy</Button>
              <Button size="sm" variant={filterMode === "days" ? "default" : "outline"} onClick={() => setFilterMode("days")} data-testid="transfers-filter-days">Días</Button>
              <Button size="sm" variant={filterMode === "range" ? "default" : "outline"} onClick={() => setFilterMode("range")} data-testid="transfers-filter-range">
                <Calendar className="w-3.5 h-3.5 mr-1" /> Rango
              </Button>
            </div>

            {filterMode === "days" && (
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 día</SelectItem>
                  <SelectItem value="3">3 días</SelectItem>
                  <SelectItem value="7">7 días</SelectItem>
                  <SelectItem value="15">15 días</SelectItem>
                  <SelectItem value="30">30 días</SelectItem>
                </SelectContent>
              </Select>
            )}

            {filterMode === "range" && (
              <div className="flex items-center gap-2">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
                <span className="text-sm text-slate-400">a</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
              </div>
            )}

            <Button onClick={handleFetch} disabled={fetching} data-testid="fetch-transfers-btn">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {fetching ? "Consultando..." : "Consultar"}
            </Button>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer ml-2" title="Modo Debug: muestra TODOS los correos del rango sin filtrar para diagnosticar por qué no aparecen los esperados">
              <input
                type="checkbox"
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
                data-testid="transfers-debug-toggle"
                className="h-3.5 w-3.5"
              />
              Debug
            </label>

            <Button
              size="sm"
              variant="outline"
              onClick={() => setIbanModalOpen(true)}
              data-testid="configure-iban-btn"
              title="Configurar IBAN propio de esta caja (para detectar entradas vs salidas)"
            >
              <Banknote className="w-3.5 h-3.5 mr-1" />
              IBAN {selectedBox?.bank_account_iban ? "✓" : "—"}
            </Button>

            <div className="flex-1" />

            {transfers.length > 0 && (
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-48"
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {transfers.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-slate-50"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-slate-700">{transfers.length}</p><p className="text-xs text-slate-500 font-medium">Total Transferencias</p></CardContent></Card>
          <Card className="bg-blue-50"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-blue-600">{fmtCRC(totalAmount)}</p><p className="text-xs text-blue-700 font-medium">Monto Total</p></CardContent></Card>
          <Card className="bg-green-50"><CardContent className="py-4 text-center"><p className="text-3xl font-bold text-green-600">{verifiedCount}/{transfers.length}</p><p className="text-xs text-green-700 font-medium">Verificadas</p></CardContent></Card>
        </div>
      )}

      {/* Table */}
      {filtered.length > 0 ? (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="transfers-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Estado</th>
                  <th className="p-2 text-left">Dirección</th>
                  <th className="p-2 text-left">Banco</th>
                  <th className="p-2 text-left">Fecha/Hora</th>
                  <th className="p-2 text-left">Documento</th>
                  <th className="p-2 text-left">Cuenta Origen</th>
                  <th className="p-2 text-left">Cuenta Destino</th>
                  <th className="p-2 text-right">Monto</th>
                  <th className="p-2 text-left">Detalle</th>
                  <th className="p-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const verified = verifiedIds.has(t.email_id);
                  const isSal = t.direction === "saliente";
                  return (
                    <tr key={t.email_id} className="border-b hover:bg-slate-50">
                      <td className="p-2">
                        {verified ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-0.5 rounded-full text-xs font-semibold">
                            <Check className="w-3 h-3" /> Verificada
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full text-xs font-semibold">
                            Pendiente
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          isSal ? "text-red-700 bg-red-100" : "text-green-700 bg-green-100"
                        }`}>
                          {isSal ? "↑ Saliente" : "↓ Entrante"}
                        </span>
                      </td>
                      <td className="p-2 font-medium">{t.bank}</td>
                      <td className="p-2 text-xs">
                        <div>{t.fecha || "—"}</div>
                        <div className="text-slate-500">{t.hora || ""}</div>
                      </td>
                      <td className="p-2 font-mono text-xs">{t.documento || "—"}</td>
                      <td className="p-2 font-mono text-xs">{t.cuenta_origen || "—"}</td>
                      <td className="p-2 font-mono text-xs">{t.cuenta_destino || "—"}</td>
                      <td className="p-2 text-right font-mono font-semibold">{fmtCRC(t.monto)}</td>
                      <td className="p-2 text-xs max-w-[180px] truncate" title={t.detalle}>{t.detalle || t.beneficiario || "—"}</td>
                      <td className="p-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openBody(t)} title="Ver cuerpo del correo">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {verified ? (
                            <Button size="sm" variant="outline" onClick={() => handleUnverify(t)} className="text-red-600 border-red-200" data-testid={`unverify-btn-${t.email_id}`}>
                              Quitar
                            </Button>
                          ) : (
                            <Button size="sm" onClick={() => openMatchModal(t)} className="bg-blue-600 hover:bg-blue-700" data-testid={`verify-btn-${t.email_id}`}>
                              <Link2 className="w-3.5 h-3.5 mr-1" /> Verificar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : transfers.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">Sin transferencias. Haga click en <strong>Consultar</strong>.</CardContent></Card>
      ) : (
        <Card><CardContent className="py-10 text-center text-slate-500">No hay coincidencias para la búsqueda.</CardContent></Card>
      )}

      {/* Match Modal */}
      <Dialog open={matchModalOpen} onOpenChange={setMatchModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vincular transferencia con transacción</DialogTitle>
          </DialogHeader>
          {matchTransfer && (
            <div className="space-y-3">
              <div className="bg-slate-50 p-3 rounded text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><strong>Banco:</strong> {matchTransfer.bank}</div>
                  <div><strong>Documento:</strong> {matchTransfer.documento}</div>
                  <div><strong>Fecha:</strong> {matchTransfer.fecha} {matchTransfer.hora}</div>
                  <div><strong>Monto:</strong> {fmtCRC(matchTransfer.monto)}</div>
                  <div className="col-span-2"><strong>Detalle:</strong> {matchTransfer.detalle || "—"}</div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium mb-2">Selecciona la transacción de Entrada/Salida de Banco que corresponde:</p>
                {loadingCandidates ? (
                  <div className="flex justify-center p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : candidates.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No hay transacciones entrada/salida de banco en esa fecha.</p>
                ) : (
                  <div className="border rounded max-h-80 overflow-y-auto divide-y">
                    {candidates.map((c) => {
                      const amountClose = Math.abs((c.amount_colones || 0) - matchTransfer.monto) < 1;
                      return (
                        <div
                          key={c.id}
                          className={`p-2 hover:bg-slate-50 flex justify-between items-center ${amountClose ? "bg-yellow-50" : ""}`}
                          data-testid={`candidate-tx-${c.id}`}
                        >
                          <div className="flex-1 text-sm">
                            <div className="font-medium">
                              {c.type === "entrada_banco" ? "↓ Entrada" : "↑ Salida"} de Banco — {fmtCRC(c.amount_colones)}
                              {amountClose && <span className="ml-2 text-xs text-amber-600 font-bold">★ MISMO MONTO</span>}
                            </div>
                            <div className="text-xs text-slate-500">{c.created_at?.slice(0, 16)} · {c.description || ""}</div>
                          </div>
                          <Button size="sm" onClick={() => handleVerify(c.id)}>
                            Vincular
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchModalOpen(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw Body Modal */}
      <Dialog open={bodyModalOpen} onOpenChange={setBodyModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Cuerpo del correo</DialogTitle>
          </DialogHeader>
          {bodyContent && (
            <div className="space-y-2 text-sm">
              <div><strong>De:</strong> {bodyContent.from}</div>
              <div><strong>Asunto:</strong> {bodyContent.subject}</div>
              <pre className="bg-slate-50 p-3 rounded text-xs whitespace-pre-wrap max-h-96 overflow-y-auto">{bodyContent.body}</pre>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* IBAN Config Modal */}
      <Dialog open={ibanModalOpen} onOpenChange={setIbanModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>IBAN propio de esta caja</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Indica el IBAN de la cuenta bancaria de <strong>{selectedBox?.name}</strong>.
              Sirve para que el verificador detecte si una transferencia es <strong>entrante</strong> (tu cuenta como destino) o <strong>saliente</strong> (tu cuenta como origen).
            </p>
            <Input
              placeholder="CR00000000000000000000"
              value={ibanInput}
              onChange={(e) => setIbanInput(e.target.value.toUpperCase())}
              data-testid="iban-input"
            />
            <p className="text-xs text-slate-500">22 caracteres iniciando con CR. Ejemplo: CR86015202001415463001</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIbanModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveIban} data-testid="save-iban-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
